import * as React from 'react';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Button } from '../button/Button';
import { getFormGridClassNames } from '../grid/FormGrid';
import { Icon } from '../icon/Icon';
import { mcClose } from '@mc/react-ui-icons';
import { ClosableDialogProps } from '../modal/ModalManager';
import { DialogBody } from './DialogBody';
import { BaseProps } from '../common/props';
import { createPortal } from 'react-dom';
import { CSSProperties, useEffect } from 'react';

export interface IDialogProps extends BaseProps {}

type DialogProps = IDialogProps & {
  /**
   * Inline dialog ve stránce - např. pro login dialog umístěný uvnitř stránky.
   */
  inline?: boolean;
  isOpen?: boolean;
  /**
   * Minimální šířka v případě dynamické velikosti dialogu, výchozí hodnota je 300. Lze zadat i v %.
   * POZOR - při použítí se pak stane, že při zmenšení okna je dialog oříznut, raději se nastavení této hodnoty vyhněte.
   * @deprecated
   */
  dynamicMinWidth?: number | string;
  /**
   * Minimální šířka v případě dynamické velikosti dialogu, výchozí hodnota je 600. Lze zadat i v %.
   */
  dynamicMaxWidth?: number | string;
  /**
   * Minimální výška v případě dynamické velikosti dialogu, výchozí hodnota není. Lze zadat i v %.
   */
  dynamicMaxHeight?: number | string;
  fullscreen?: boolean;
  size?:
    | undefined
    // @deprecated
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'xxl'
    | 'dblCol'
    /**
     * Dynamická velikost dialogu, je vhodné pak nastavit dynamicMinWidth a dynamicMaxWidth pokud nevyhovují, případně pokud je potřeba řešit výšku, tak dynamicMaxHeight.
     */
    | 'dynamic'
    | 'fullscreen';
  /**
   * Extra prvky hned napravo od nadpisu, např nějaké akce apod. Pokud někdo chce aby ty akce byly napravo, tak se to dá přepnout pomocí extraRight.
   */
  extra?: React.ReactNode;
  /**
   * Extra akce nejsou hned napravo od nadpisu, ale napravo v celé oblasti titulku (tedy úplně napravo).
   */
  extraRight?: boolean;
  /**
   * Standardně zobrazovat nahoře, typické pro dialogy, kde roste výška (pak neskáčou po obrazovce při změně velikosti).
   */
  showOnTop?: boolean;
  /**
   * Classname vysoko v obalovači dialogu  - portál.
   */
  portalClassName?: string;
  isCloseButtonShown?: boolean;
  escapeKeyClose?: boolean;
  outsideClickClose?: boolean;
  /**
   * Zapne automatický focus prvního elementu v DOM, ve výchozím stavu zapnuto.
   */
  autoFocus?: boolean;
} & ClosableDialogProps;

/**
 * Standardní chování dialogu:
 * - dialog zobrazí jako open
 * - ESC ho nezavírá
 * - kliknutí mimo dialog ho nezavírá
 */
export const Dialog: React.FC<DialogProps> = ({
  title,
  className,
  portalClassName,
  showOnTop,
  style,
  escapeKeyClose,
  onClose,
  inline,
  dataTest,
  isOpen = true,
  isCloseButtonShown = undefined,
  outsideClickClose,
  size,
  extra,
  extraRight,
  dynamicMinWidth = 300,
  dynamicMaxWidth = 600,
  dynamicMaxHeight,
  autoFocus = true,
  children,
}) => {
  const showCloseButton =
    isCloseButtonShown !== undefined
      ? isCloseButtonShown
      : size === 'fullscreen';

  let dialogTitle;
  if (title) {
    dialogTitle = (
      <div
        className={classNames(Classes.DIALOG_HEADER_WRAPPER, {
          [Classes.DIALOG_HEADER_EXTRA_RIGHT]: extraRight,
        })}
      >
        <div className={Classes.DIALOG_HEADER_TITLE}>{title}</div>
        <div>{extra}</div>
        {showCloseButton && !inline && (
          <div className={Classes.DIALOG_HEADER_CLOSE}>
            <Button
              // inheritColor
              style="link"
              onClick={(e) => onClose && onClose(e)}
              dataTest={'close'}
              icon={<Icon fixedWidth icon={mcClose} />}
            />
          </div>
        )}
      </div>
    );
  }

  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const dialogBodyRef = React.useRef<HTMLDivElement | null>(null);
  const [showTopScrollIndicator, setShowTopScrollIndicator] =
    React.useState<boolean>(false);
  const [showBottomScrollIndicator, setShowBottomScrollIndicator] =
    React.useState<boolean>(false);
  const [saveListenerAdded, setSaveListenerAdded] =
    React.useState<boolean>(false);
  const [scrollListenerAdded, setScrollListenerAdded] =
    React.useState<boolean>(false);
  const [focusHandled, setFocusHandled] = React.useState<boolean>(false);

  const handleAddAutoFocus = () => {
    if (!dialogBodyRef.current || focusHandled) return;
    setFocusHandled(true);
    const firstInput = dialogBodyRef.current.querySelector(`
      .${Classes.STRING_INPUT} input,
      textarea.${Classes.TEXT_AREA_INPUT},
      .${Classes.NUMBER_INPUT} input,
      .${Classes.PASSWORD_INPUT} input,
      .${Classes.DATE_INPUT} input,
      .${Classes.TIME_INPUT} input,
      .${Classes.DAY_MONTH_INPUT} input,
      .${Classes.DATE_TIME_INPUT} input,
      .${Classes.AUTOCOMPLETE} input,
      .${Classes.SELECTBOX} input
    `);
    if (!firstInput) {
      return;
    }
    if (firstInput === document.activeElement) {
      return;
    }
    (firstInput as HTMLElement).focus();
  };

  const saveCallback = React.useCallback((e: KeyboardEvent) => {
    if (!dialogRef.current || !dialogBodyRef.current) {
      return;
    }
    if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') {
      return;
    }
    e.preventDefault();

    const domNode = dialogRef.current;
    if (!domNode || !domNode.parentNode || !(domNode instanceof Element)) {
      return;
    }

    const portalElement = domNode.closest(`.${Classes.PORTAL_DIALOG}`);
    if (!portalElement) {
      return;
    }

    const isLastDialog = !portalElement.nextSibling;
    if (!isLastDialog) {
      return;
    }

    const footerPrimaryButtons = domNode.querySelectorAll(
      `.${Classes.DIALOG_FOOTER} .${Classes.BUTTON}.${Classes.BUTTON_PRIMARY}`
    );
    // Chceme volat pouze pro dialogy s jedním primary buttonem, aby nedošlo k dilematu, jaký button volat.
    if (footerPrimaryButtons.length !== 1) {
      return;
    }
    (footerPrimaryButtons[0] as HTMLElement).click();
  }, []);

  const scrollCallback = React.useCallback(() => {
    if (!dialogBodyRef.current) return;
    const height = Math.round(
      dialogBodyRef.current.getBoundingClientRect().height
    );
    const scrollHeight = Math.round(dialogBodyRef.current.scrollHeight);
    const scrollTop = Math.round(dialogBodyRef.current.scrollTop);

    setShowTopScrollIndicator(scrollTop > 0);
    setShowBottomScrollIndicator(scrollTop + height + 5 < scrollHeight);
  }, []);

  const addScrollListener = () => {
    if (!dialogBodyRef.current || scrollListenerAdded) return;
    setScrollListenerAdded(true);
    dialogBodyRef.current.addEventListener('scroll', scrollCallback);
  };

  const addSaveListener = () => {
    if (!dialogRef.current || saveListenerAdded) return;
    setSaveListenerAdded(true);
    document.addEventListener('keydown', saveCallback);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', saveCallback);
      setSaveListenerAdded(false);
      if (dialogBodyRef.current) {
        dialogBodyRef.current.removeEventListener('scroll', scrollCallback);
        setScrollListenerAdded(false);
        setFocusHandled(false);
      }
    };
  }, []);

  // useEffect(() => {
  //   document.body.addEventListener('keyup', (e) => {
  //     console.log('Bkeyup', e);
  //   });
  //   document.body.addEventListener('keydown', (e) => {
  //     console.log('Bkeydown', e);
  //   });
  //   document.addEventListener('keyup', (e) => {
  //     console.log('Dkeyup', e);
  //   });
  //   document.addEventListener('keydown', (e) => {
  //     console.log('Dkeydown', e);
  //   });
  // }, []);

  if (!isOpen) {
    return null;
  }

  // Dynamické a fullscreen styly
  const dialogSizeStyles: CSSProperties = {};
  if (size === 'dynamic') {
    dialogSizeStyles.minWidth = dynamicMinWidth;
    dialogSizeStyles.maxWidth = dynamicMaxWidth;

    if (dynamicMaxHeight !== undefined) {
      dialogSizeStyles.maxHeight = dynamicMaxHeight;
    }
  } else if (size === 'fullscreen') {
    dialogSizeStyles.width = '100%';
    dialogSizeStyles.minWidth = '100%';
    dialogSizeStyles.maxWidth = '100%';

    dialogSizeStyles.height = '100%';
    dialogSizeStyles.minHeight = '100%';
    dialogSizeStyles.maxHeight = '100%';
  }

  if (inline) {
    return (
      <div className={Classes.PORTAL_DIALOG_INLINE_WRAPPER}>
        <div
          className={Classes.PORTAL_DIALOG_CONTAINER}
          style={{
            height: size === 'fullscreen' ? '100%' : undefined,
          }}
        >
          <div
            style={{
              ...style,
              ...dialogSizeStyles,
            }}
            className={classNames(
              Classes.DIALOG,
              className,
              getFormGridClassNames(size),
              {
                [Classes.DIALOG_FORMGRID]: size !== 'dynamic',
                [Classes.DIALOG_SIZE_DEFAULT]: !size || size === 'sm',
                [Classes.DIALOG_SIZE_DYNAMIC]: size === 'dynamic',
                [Classes.DIALOG_SIZE_MD]: size === 'md',
                [Classes.DIALOG_SIZE_LG]: size === 'lg',
                [Classes.DIALOG_SIZE_XL]: size === 'xl',
                [Classes.DIALOG_SIZE_XXL]: size === 'xxl',
                [Classes.DIALOG_SIZE_DBLCOL]: size === 'dblCol',
                [Classes.DIALOG_SHOW_TOP_SCROLL_INDICATOR]:
                  showTopScrollIndicator,
                [Classes.DIALOG_SHOW_BOTTOM_SCROLL_INDICATOR]:
                  showBottomScrollIndicator,
              }
            )}
            ref={(ref: any): void => {
              if (ref) {
                dialogRef.current = ref;
                addSaveListener();
              }
            }}
          >
            {dialogTitle && (
              <div className={Classes.DIALOG_HEADER}>
                <h4 className={Classes.DIALOG_HEADER_HEADING}>{dialogTitle}</h4>
              </div>
            )}
            {children
              ? React.Children.toArray(children).map((child) =>
                  React.isValidElement(child) && child.type === DialogBody
                    ? React.cloneElement(child, {
                        // @ts-ignore
                        divRef: (ref) => {
                          dialogBodyRef.current = ref;
                          addScrollListener();
                          if (ref && autoFocus) {
                            handleAddAutoFocus();
                          }
                        },
                      })
                    : child
                )
              : null}
          </div>
        </div>
      </div>
    );
  } else {
    const dialog = (
      <div
        data-test={dataTest ? `dialog-${dataTest}` : undefined}
        className={classNames(
          Classes.DIALOG,
          className,
          getFormGridClassNames(size),
          {
            [Classes.DIALOG_FULLSCREEN]: size === 'fullscreen',
            [Classes.DIALOG_FORMGRID]: size !== 'dynamic',
            [Classes.DIALOG_ON_TOP]: showOnTop,
            [Classes.DIALOG_SIZE_DEFAULT]: !size || size === 'sm',
            [Classes.DIALOG_SIZE_DYNAMIC]: size === 'dynamic',
            [Classes.DIALOG_SIZE_MD]: size === 'md',
            [Classes.DIALOG_SIZE_LG]: size === 'lg',
            [Classes.DIALOG_SIZE_XL]: size === 'xl',
            [Classes.DIALOG_SIZE_XXL]: size === 'xxl',
            [Classes.DIALOG_SIZE_DBLCOL]: size === 'dblCol',
            [Classes.DIALOG_SHOW_TOP_SCROLL_INDICATOR]: showTopScrollIndicator,
            [Classes.DIALOG_SHOW_BOTTOM_SCROLL_INDICATOR]:
              showBottomScrollIndicator,
          }
        )}
        style={{
          ...style,
          ...dialogSizeStyles,
        }}
        ref={(ref: any): void => {
          if (ref) {
            dialogRef.current = ref;
            addSaveListener();
          }
        }}
      >
        {dialogTitle && (
          <div className={Classes.DIALOG_HEADER}>
            <h4 className={Classes.DIALOG_HEADER_HEADING}>{dialogTitle}</h4>
          </div>
        )}
        {children
          ? React.Children.toArray(children).map((child) =>
              React.isValidElement(child) && child.type === DialogBody
                ? React.cloneElement(child, {
                    // @ts-ignore
                    divRef: (ref) => {
                      if (ref) {
                        dialogBodyRef.current = ref;
                        addScrollListener();
                        if (ref && autoFocus) {
                          handleAddAutoFocus();
                        }
                      }
                    },
                  })
                : child
            )
          : null}
      </div>
    );

    const modalRoot =
      document.getElementById('ui-v4-modal-root') ?? document.body;

    return createPortal(
      <div
        data-test={dataTest ? `portal-dialog-${dataTest}` : undefined}
        onKeyUp={(e) => {
          if (escapeKeyClose && e.key === 'Escape') {
            // @ts-ignore
            onClose && onClose(e);
          }
        }}
        className={classNames(Classes.PORTAL_DIALOG, portalClassName)}
      >
        <div className={Classes.PORTAL_DIALOG_OVERLAY}>
          <div
            className={Classes.PORTAL_DIALOG_BACKDROP}
            onClick={(e) => {
              if (outsideClickClose) {
                onClose && onClose(e);
              }
            }}
          ></div>
          <div
            className={Classes.PORTAL_DIALOG_CONTAINER}
            style={{
              height: size === 'fullscreen' ? '100%' : undefined,
            }}
          >
            {dialog}
          </div>
        </div>
      </div>,
      modalRoot
    );
  }
};
